import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, LocaleSchema } from "../common/primitives.js";
import { PageQuerySchema, pageOf } from "../common/pagination.js";
const SPACE_KINDS = ["organization", "internal", "shared", "group"];
const SpaceKindSchema = z.enum(SPACE_KINDS);
const SPACE_STATUSES = ["active", "closed"];
const SpaceStatusSchema = z.enum(SPACE_STATUSES);
const SPACE_RIGHTS = ["read", "write", "approve"];
const SpaceRightsSchema = z.enum(SPACE_RIGHTS);
const SPACE_ACCESS_VIAS = [
  "host_member",
  "partner_delegation",
  "direct_invite",
  "guest",
  "partner_admin"
];
const SpaceAccessViaSchema = z.enum(SPACE_ACCESS_VIAS);
const uniqueIds = (max) => z.array(IdSchema).max(max).refine((ids) => new Set(ids).size === ids.length, "an item is listed twice");
const LimitedScopeSchema = z.strictObject({ folderIds: uniqueIds(100), channelIds: uniqueIds(100) }).refine(
  (scope) => scope.folderIds.length + scope.channelIds.length > 0,
  "choose at least one folder or channel"
);
const WholeSpaceScopeSchema = z.strictObject({ all: z.literal(true) });
const SpaceScopeSchema = z.union([WholeSpaceScopeSchema, LimitedScopeSchema]);
function isWholeSpace(scope) {
  return "all" in scope;
}
const DOMAIN_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const GuestDomainSchema = z.string().trim().toLowerCase().max(253).refine((domain) => {
  const labels = domain.split(".");
  const tld = labels.at(-1) ?? "";
  return labels.length >= 2 && /^[a-z]{2,63}$/.test(tld) && labels.slice(0, -1).every((label) => DOMAIN_LABEL.test(label));
}, "a domain such as bureau.tn");
const SpacePolicySchema = z.strictObject({
  downloadAllowed: z.boolean(),
  watermark: z.boolean(),
  printAllowed: z.boolean(),
  shareLinksAllowed: z.boolean().default(true),
  guestDomains: z.array(GuestDomainSchema).max(50).refine((list) => new Set(list).size === list.length, "a domain is listed twice")
});
const DEFAULT_SPACE_POLICY = {
  downloadAllowed: true,
  watermark: false,
  printAllowed: true,
  shareLinksAllowed: true,
  guestDomains: []
};
function spacePolicyOf(stored) {
  const raw = stored ?? {};
  const domains = z.array(GuestDomainSchema).max(50).safeParse(raw.guest_domains);
  return {
    downloadAllowed: raw.download_allowed !== false,
    watermark: raw.watermark === true,
    printAllowed: raw.print_allowed !== false,
    shareLinksAllowed: raw.share_links_allowed !== false,
    guestDomains: domains.success ? domains.data : []
  };
}
function storedSpacePolicy(policy) {
  return {
    download_allowed: policy.downloadAllowed,
    watermark: policy.watermark,
    print_allowed: policy.printAllowed,
    share_links_allowed: policy.shareLinksAllowed,
    guest_domains: policy.guestDomains
  };
}
const SpaceNameSchema = z.string().trim().min(1).max(200);
const SpacePartnerParamsSchema = z.strictObject({ spaceId: IdSchema, partnerId: IdSchema });
const SpaceMemberParamsSchema = z.strictObject({ spaceId: IdSchema, memberId: IdSchema });
const SpaceInvitationParamsSchema = z.strictObject({ invitationId: IdSchema });
const SpaceInvitationOfSpaceParamsSchema = z.strictObject({
  spaceId: IdSchema,
  invitationId: IdSchema
});
const SpaceApprovalParamsSchema = z.strictObject({
  orgId: IdSchema,
  invitationId: IdSchema
});
const CreateSpaceRequestSchema = z.strictObject({
  kind: z.enum(["shared", "group"]),
  name: SpaceNameSchema,
  projectId: IdSchema.optional()
}).refine((body) => body.kind === "shared" === (body.projectId !== void 0), {
  message: "a shared space is linked to a project; a group is not",
  path: ["projectId"]
});
const UpdateSpaceRequestSchema = z.strictObject({ name: SpaceNameSchema.optional(), policy: SpacePolicySchema.optional() }).refine((body) => body.name !== void 0 || body.policy !== void 0, "change something");
const SpaceHostSchema = z.strictObject({
  organizationId: IdSchema,
  name: z.string(),
  logoUrl: z.string().nullable()
});
const SpaceAccessSchema = z.strictObject({
  external: z.boolean(),
  via: SpaceAccessViaSchema,
  rights: SpaceRightsSchema.nullable(),
  expiresAt: IsoDateTimeSchema.nullable(),
  /** The organisation the caller acts for (a partner), null for host members and guests. */
  organizationId: IdSchema.nullable(),
  /** The partnership the caller belongs to or administers. */
  partnerId: IdSchema.nullable()
});
const SPACE_TABS = [
  "documents",
  // COLX: the mandataire's synthesis and matrix, the groupement (ADR-030).
  "synthesis",
  "groupement",
  "chat",
  "calendar",
  "meetings",
  "people",
  "partners",
  "delegates",
  "settings",
  "audit"
];
const SpaceTabSchema = z.enum(SPACE_TABS);
const SPACE_PERMISSIONS = [
  "can_view",
  "can_contribute",
  "can_manage",
  "can_delegate"
];
const SpaceSchema = z.strictObject({
  id: IdSchema,
  name: z.string(),
  kind: SpaceKindSchema,
  status: SpaceStatusSchema,
  projectId: IdSchema.nullable(),
  closedAt: IsoDateTimeSchema.nullable(),
  policy: SpacePolicySchema,
  host: SpaceHostSchema,
  access: SpaceAccessSchema,
  tabs: z.array(SpaceTabSchema),
  _permissions: z.array(z.enum(SPACE_PERMISSIONS))
});
const MySpaceSchema = z.strictObject({
  id: IdSchema,
  name: z.string(),
  kind: SpaceKindSchema,
  status: SpaceStatusSchema,
  projectId: IdSchema.nullable(),
  host: SpaceHostSchema,
  external: z.boolean(),
  via: SpaceAccessViaSchema,
  rights: SpaceRightsSchema.nullable(),
  expiresAt: IsoDateTimeSchema.nullable()
});
const MySpaceListSchema = z.strictObject({ items: z.array(MySpaceSchema) });
const SPACE_PARTNER_STATUSES = [
  "pending",
  "active",
  "declined",
  "expired",
  "revoked"
];
const SpacePartnerStatusSchema = z.enum(SPACE_PARTNER_STATUSES);
const PartnerLookupQuerySchema = z.strictObject({ taxId: z.string().trim().min(5).max(40) });
const PartnerLookupSchema = z.strictObject({ organizationId: IdSchema, name: z.string() });
const FutureDateTimeSchema = IsoDateTimeSchema;
const InvitePartnerRequestSchema = z.strictObject({
  taxId: z.string().trim().min(5).max(40).optional(),
  organizationId: IdSchema.optional(),
  scope: SpaceScopeSchema,
  rights: SpaceRightsSchema,
  memberCap: z.int().min(1).max(500),
  /** When the partnership ends (null: until revoked). */
  expiresAt: FutureDateTimeSchema.nullable()
}).refine((r) => r.taxId === void 0 !== (r.organizationId === void 0), {
  message: "give either a taxId or an organizationId (from the directory)",
  path: ["taxId"]
});
const SpacePartnerSchema = z.strictObject({
  id: IdSchema,
  organizationId: IdSchema,
  organizationName: z.string(),
  scope: SpaceScopeSchema,
  rights: SpaceRightsSchema,
  memberCap: z.int(),
  activeMembers: z.int(),
  expiresAt: IsoDateTimeSchema.nullable(),
  status: SpacePartnerStatusSchema,
  createdAt: IsoDateTimeSchema
});
const SpacePartnerListSchema = z.strictObject({ items: z.array(SpacePartnerSchema) });
const ScopeItemSchema = z.strictObject({
  id: IdSchema,
  kind: z.enum(["folder", "channel"]),
  name: z.string()
});
const PartnershipSchema = z.strictObject({
  id: IdSchema,
  spaceId: IdSchema,
  spaceName: z.string(),
  host: SpaceHostSchema,
  scope: SpaceScopeSchema,
  rights: SpaceRightsSchema,
  memberCap: z.int(),
  activeMembers: z.int(),
  expiresAt: IsoDateTimeSchema.nullable(),
  status: SpacePartnerStatusSchema,
  createdAt: IsoDateTimeSchema,
  /** The folders and channels of a limited scope, with their names (empty for the whole space). */
  scopeItems: z.array(ScopeItemSchema)
});
const PartnershipListSchema = z.strictObject({ items: z.array(PartnershipSchema) });
const DelegateMemberRequestSchema = z.strictObject({
  userId: IdSchema,
  scope: SpaceScopeSchema,
  rights: SpaceRightsSchema
});
const SPACE_MEMBER_VIAS = [
  "host_member",
  "partner_delegation",
  "direct_invite",
  "guest"
];
const SpaceMemberViaSchema = z.enum(SPACE_MEMBER_VIAS);
const SpaceMemberSchema = z.strictObject({
  id: IdSchema,
  userId: IdSchema,
  displayName: z.string(),
  via: SpaceMemberViaSchema,
  /** The partner organisation the person acts for. */
  organizationId: IdSchema.nullable(),
  organizationName: z.string().nullable(),
  scope: SpaceScopeSchema,
  rights: SpaceRightsSchema,
  expiresAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  _permissions: z.array(z.enum(["can_revoke"]))
});
const SpaceMemberListSchema = z.strictObject({ items: z.array(SpaceMemberSchema) });
const SpaceMemberListQuerySchema = z.strictObject({ partnerId: IdSchema.optional() });
const SPACE_INVITATION_KINDS = ["organization", "nominative", "guest"];
const SpaceInvitationKindSchema = z.enum(SPACE_INVITATION_KINDS);
const SPACE_INVITATION_STATUSES = [
  "pending_approval",
  "pending",
  "accepted",
  "declined",
  "expired",
  "revoked"
];
const SpaceInvitationStatusSchema = z.enum(SPACE_INVITATION_STATUSES);
const GUEST_ACCESS_DAYS = 90;
const CreateSpaceInvitationRequestSchema = z.strictObject({
  kind: z.enum(["nominative", "guest"]),
  email: z.email().max(320),
  scope: SpaceScopeSchema,
  rights: SpaceRightsSchema,
  /** A named access's end (null: until revoked); a guest always gets 90 days. */
  accessExpiresAt: IsoDateTimeSchema.nullable().optional(),
  locale: LocaleSchema.optional()
}).superRefine((body, ctx) => {
  if (body.kind === "guest" && isWholeSpace(body.scope))
    ctx.addIssue({
      code: "custom",
      path: ["scope"],
      message: "a guest is limited to some channels or folders"
    });
  if (body.kind === "guest" && body.accessExpiresAt != null)
    ctx.addIssue({
      code: "custom",
      path: ["accessExpiresAt"],
      message: "a guest access lasts 90 days (extendable)"
    });
});
const SpaceInvitationSchema = z.strictObject({
  id: IdSchema,
  kind: SpaceInvitationKindSchema,
  email: z.string().nullable(),
  organizationName: z.string().nullable(),
  scope: SpaceScopeSchema,
  rights: SpaceRightsSchema,
  status: SpaceInvitationStatusSchema,
  requiresHomeApproval: z.boolean(),
  accessExpiresAt: IsoDateTimeSchema.nullable(),
  expiresAt: IsoDateTimeSchema,
  createdAt: IsoDateTimeSchema
});
const SpaceInvitationListSchema = z.strictObject({ items: z.array(SpaceInvitationSchema) });
const IncomingSpaceInvitationSchema = z.strictObject({
  id: IdSchema,
  kind: SpaceInvitationKindSchema,
  host: SpaceHostSchema,
  spaceId: IdSchema,
  spaceName: z.string(),
  /** For a partnership: the organisation invited (one the caller administers). */
  organizationId: IdSchema.nullable(),
  organizationName: z.string().nullable(),
  scope: SpaceScopeSchema,
  rights: SpaceRightsSchema,
  memberCap: z.int().nullable(),
  accessExpiresAt: IsoDateTimeSchema.nullable(),
  expiresAt: IsoDateTimeSchema,
  invitedByName: z.string().nullable(),
  createdAt: IsoDateTimeSchema
});
const IncomingSpaceInvitationListSchema = z.strictObject({
  items: z.array(IncomingSpaceInvitationSchema)
});
const AcceptedSpaceInvitationSchema = z.strictObject({
  spaceId: IdSchema,
  /** The organisation that hosts the space. */
  hostOrganizationId: IdSchema,
  /** For a partnership: the organisation that became a partner. */
  partnerOrganizationId: IdSchema.nullable()
});
const SpaceApprovalSchema = z.strictObject({
  id: IdSchema,
  host: z.strictObject({ organizationId: IdSchema, name: z.string() }),
  spaceId: IdSchema,
  spaceName: z.string(),
  kind: SpaceInvitationKindSchema,
  email: z.string(),
  inviteeName: z.string().nullable(),
  rights: SpaceRightsSchema,
  accessExpiresAt: IsoDateTimeSchema.nullable(),
  invitedByName: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema
});
const SpaceApprovalListSchema = z.strictObject({ items: z.array(SpaceApprovalSchema) });
const ExtendSpaceMemberResponseSchema = z.strictObject({ expiresAt: IsoDateTimeSchema });
const SpaceAuditQuerySchema = PageQuerySchema;
const SpaceAuditEventSchema = z.strictObject({
  id: IdSchema,
  occurredAt: IsoDateTimeSchema,
  actorUserId: IdSchema.nullable(),
  actorName: z.string().nullable(),
  action: z.string(),
  resourceType: z.string(),
  resourceId: z.string().nullable(),
  /** The partner organisation the event concerns, when it concerns one. */
  partnerOrganizationId: IdSchema.nullable()
});
const SpaceAuditPageSchema = pageOf(SpaceAuditEventSchema);
export * from "./collaboration.js";
export {
  AcceptedSpaceInvitationSchema,
  CreateSpaceInvitationRequestSchema,
  CreateSpaceRequestSchema,
  DEFAULT_SPACE_POLICY,
  DelegateMemberRequestSchema,
  ExtendSpaceMemberResponseSchema,
  GUEST_ACCESS_DAYS,
  GuestDomainSchema,
  IncomingSpaceInvitationListSchema,
  IncomingSpaceInvitationSchema,
  InvitePartnerRequestSchema,
  LimitedScopeSchema,
  MySpaceListSchema,
  MySpaceSchema,
  PartnerLookupQuerySchema,
  PartnerLookupSchema,
  PartnershipListSchema,
  PartnershipSchema,
  SPACE_ACCESS_VIAS,
  SPACE_INVITATION_KINDS,
  SPACE_INVITATION_STATUSES,
  SPACE_KINDS,
  SPACE_MEMBER_VIAS,
  SPACE_PARTNER_STATUSES,
  SPACE_PERMISSIONS,
  SPACE_RIGHTS,
  SPACE_STATUSES,
  SPACE_TABS,
  ScopeItemSchema,
  SpaceAccessSchema,
  SpaceAccessViaSchema,
  SpaceApprovalListSchema,
  SpaceApprovalParamsSchema,
  SpaceApprovalSchema,
  SpaceAuditEventSchema,
  SpaceAuditPageSchema,
  SpaceAuditQuerySchema,
  SpaceHostSchema,
  SpaceInvitationKindSchema,
  SpaceInvitationListSchema,
  SpaceInvitationOfSpaceParamsSchema,
  SpaceInvitationParamsSchema,
  SpaceInvitationSchema,
  SpaceInvitationStatusSchema,
  SpaceKindSchema,
  SpaceMemberListQuerySchema,
  SpaceMemberListSchema,
  SpaceMemberParamsSchema,
  SpaceMemberSchema,
  SpaceMemberViaSchema,
  SpacePartnerListSchema,
  SpacePartnerParamsSchema,
  SpacePartnerSchema,
  SpacePartnerStatusSchema,
  SpacePolicySchema,
  SpaceRightsSchema,
  SpaceSchema,
  SpaceScopeSchema,
  SpaceStatusSchema,
  SpaceTabSchema,
  UpdateSpaceRequestSchema,
  WholeSpaceScopeSchema,
  isWholeSpace,
  spacePolicyOf,
  storedSpacePolicy
};
//# sourceMappingURL=index.js.map